import type { ReactNode } from "react";
import { useId } from "react";

/**
 * Shared card shell for every block on Step 4 (Review & launch). Mirrors the
 * prototype's `.review-card` / `.review-card-header` / `.review-card-title`
 * (Campaign-Builder-Master-Prototype.html, CSS ~line 7160 onward).
 *
 * Contract is fixed — other Step 4 components depend on these exact props.
 */
export interface ReviewCardProps {
  /**
   * Omitted by the Recipient Experience card, which lost its header in the Stage 2 revisions —
   * the preview itself carries the card (prototype `.review-experience-card`, CSS ~line 7242).
   */
  title?: string;
  /** Right-hand header controls (Edit / Test Email). */
  actions?: ReactNode;
  children: ReactNode;
  /** Extra classes on the card root — used by the sticky checkout column. */
  className?: string;
  /**
   * Tighter 14px padding, as the prototype's `.review-experience-card` override does for the
   * headerless preview card. A prop rather than a `className` override because two conflicting
   * padding utilities on the same element resolve by stylesheet order, not attribute order.
   */
  compact?: boolean;
}

export default function ReviewCard({
  title,
  actions,
  children,
  className = "",
  compact = false,
}: ReviewCardProps) {
  const titleId = useId();

  return (
    <div
      className={`bg-white border border-card-border rounded-card ${compact ? "p-3.5" : "p-6"} shadow-[0_1px_2px_rgba(0,0,0,0.02)] ${className}`}
    >
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 pb-4 border-b border-card-border mb-[18px]">
          {title && (
            <h2 id={titleId} className="font-crimson text-[22px] font-normal text-rodeo-black m-0">
              {title}
            </h2>
          )}
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}
